import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageCircle } from "lucide-react";
import { Thread } from "./Thread";
import { ANDREA_PHONE, contactClick } from "../lib/contact";
import { trackEvent } from "../lib/tracking";

// Post-booking confirmation + Google Ads conversion destination.
// noindex, no nav links to it — exists only as the successful-booking landing.

// Google Ads conversion destination (send_to) — the client creates the conversion
// action in Google Ads and supplies the label (format: "AW-18426106228/xxxxxxx").
// Until then this stays null: the base Google tag still records the page visit
// (enough for a URL-rule conversion in Google Ads) and the dataLayer event below
// remains the durable GTM-ready hook.
const GOOGLE_ADS_SEND_TO = null;

export const AssessmentBooked = () => {
  const reduce = useReducedMotion();

  useEffect(() => {
    document.title = "You're Booked — The Nurtured Parent Co.";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,follow";
    document.head.appendChild(meta);
    // Completed-booking event — fire once per session, never twice per load.
    if (!sessionStorage.getItem("fsab_event_fired")) {
      sessionStorage.setItem("fsab_event_fired", "1");
      trackEvent("free_sleep_assessment_booked", { page: "/assessment-booked" });
      if (GOOGLE_ADS_SEND_TO && typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: GOOGLE_ADS_SEND_TO });
      }
    }
    // Calendly's "pass event details" setting appends invitee name/email and
    // the event time to this redirect. Forward them to the family's record —
    // PII goes ONLY to our own backend, never into analytics.
    if (!sessionStorage.getItem("ab_record_posted")) {
      const q = new URLSearchParams(window.location.search);
      const body = {
        email: q.get("invitee_email"),
        full_name: q.get("invitee_full_name"),
        first_name: q.get("invitee_first_name"),
        last_name: q.get("invitee_last_name"),
        event_start: q.get("event_start_time"),
        event_end: q.get("event_end_time"),
        event_name: q.get("event_type_name"),
      };
      if (body.email || body.full_name || body.first_name) {
        sessionStorage.setItem("ab_record_posted", "1");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assessments/booked`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch(() => {});
      }
    }
    return () => {
      document.head.removeChild(meta);
      document.title = "The Nurtured Parent Co. — Pediatric Sleep Consulting";
    };
  }, []);

  const rise = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="booked-page" className="min-h-svh bg-ivory text-ink flex flex-col">
      <header className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-8">
        <a href="/" data-testid="booked-logo" className="inline-block">
          <span className="block font-serif font-light text-xl sm:text-2xl leading-tight tracking-tight">
            The Nurtured Parent Co.
          </span>
          <span className="block mt-1 text-[9px] tracking-[0.3em] uppercase text-smoke">
            Pediatric Sleep Consulting
          </span>
        </a>
      </header>

      <main className="flex-1 flex flex-col justify-center max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 py-16">
        <motion.p {...rise(0.1)} className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
          You&rsquo;re on the calendar.
        </motion.p>

        <motion.h1 {...rise(0.25)} className="mt-6 font-serif font-light text-ink text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight">
          You&rsquo;re <span className="italic text-clay">booked.</span>
        </motion.h1>

        <motion.div {...rise(0.4)} className="mt-10 max-w-lg space-y-5 text-base sm:text-lg font-normal leading-relaxed text-smoke">
          <p>Andrea has personally reserved this time for your family.</p>
          <p>
            Come ready to talk about what&rsquo;s happening with sleep, what
            you&rsquo;ve tried, and what you&rsquo;d most like to change.
          </p>
          <p>
            If someone else will be part of the decision about moving forward,
            they&rsquo;re welcome to join you.
          </p>
          <p>
            If continued professional support makes sense after your
            assessment, Andrea will personally walk you through what she
            recommends, what&rsquo;s included, and what working together would
            look like. There is never any obligation to continue.
          </p>
        </motion.div>

        <motion.div {...rise(0.55)} className="mt-14 pt-12 border-t border-ink/10 max-w-xl">
          <h2 className="font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
            Have something you want
            <span className="block">Andrea to know before you talk?</span>
          </h2>
          <a
            data-testid="booked-text-cta"
            href={`sms:${ANDREA_PHONE}`}
            onClick={(e) => {
              trackEvent("text_andrea_click", { location: "booked_page" });
              contactClick("text")(e);
            }}
            className="mt-7 inline-flex items-center gap-3 rounded-full bg-ink text-ivory font-medium px-8 py-4 text-base hover:bg-espresso transition-colors duration-300 group"
          >
            <MessageCircle size={17} aria-hidden="true" />
            Text Andrea
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <div>
            <a
              data-testid="booked-home-link"
              href="/"
              className="mt-7 inline-flex items-center gap-2 py-3 -my-3 text-sm font-normal text-smoke underline underline-offset-8 decoration-ink/20 hover:decoration-clay hover:text-ink transition-[text-decoration-color,color] duration-300"
            >
              Back to The Nurtured Parent Co.
              <ArrowRight size={13} />
            </a>
          </div>
        </motion.div>
      </main>

      <div className="max-w-[1600px] w-full mx-auto px-6 md:px-12 lg:px-20 pb-10">
        <Thread className="text-clay/40" />
      </div>
    </div>
  );
};
